import { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { datadogRum } from '@datadog/browser-rum';
import { faTrashAlt, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';

import {
    useDeleteImageForResourceMutation,
    useUploadImageForResourceMutation,
} from 'src/store/sageAdminApi';
import { snackActions } from 'src/utils/snackBarTools';

interface AddEditAvatarImageProps {
    id?: string;
    onComplete: (imageData?: string) => void;
}

type Crop = {
    unit: '%' | 'px';
    x: number;
    y: number;
    width: number;
    height: number;
};

type PixelCrop = {
    x: number;
    y: number;
    width: number;
    height: number;
};

const aspect = 1;

const AddEditAvatarImage = ({ id, onComplete }: AddEditAvatarImageProps) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [crop, setCrop] = useState<Crop>({
        unit: 'px',
        width: 100,
        x: 25,
        y: 25,
        height: 100,
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [upImg, setUpImg] = useState<string>('');

    const [uploadImageForResource, { isLoading: isUploadingImage }] =
        useUploadImageForResourceMutation();
    const [deleteImageForResource, { isLoading: isDeletingImage }] =
        useDeleteImageForResourceMutation();

    async function uploadImage(imageData: string) {
        if (!id) return;

        try {
            const imageDataString = String(imageData);
            await uploadImageForResource({ resourceId: id, body: imageDataString }).unwrap();
            snackActions.success(`Photo successfully uploaded`);
            onComplete(imageDataString);
        } catch (error) {
            datadogRum.addError(error, { message: 'Error uploading image', resourceId: id });
            snackActions.error(`An error occurred: ${error}`);
        } finally {
            setShowImageDialog(false);
        }
    }

    const handleImageSave = (canvas: HTMLCanvasElement | null, userCrop: PixelCrop | null) => {
        if (!userCrop || !canvas) {
            setShowImageDialog(false);
            return;
        }

        // Get the base64 image data
        const imgToBase64 = canvas.toDataURL('image/jpeg', 0.75);
        if (!id) {
            // If we don't have an id, we're creating a new person, so just return the base64 data
            onComplete(imgToBase64);
        } else {
            uploadImage(imgToBase64);
        }
    };

    async function deleteImage() {
        if (!id) return;

        try {
            await deleteImageForResource({ resourceId: id }).unwrap();
            // Passing in an empty string will trigger the Person component to update the sessionStorage cache for this resourceId
            onComplete('');
            snackActions.success(`Photo successfully deleted`);
        } catch (error) {
            datadogRum.addError(error, { message: 'Error deleting image', resourceId: id });
            snackActions.error(`An error occurred: ${error}`);
        } finally {
            setShowImageDialog(false);
        }
    }

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => setUpImg(reader.result as string));
            reader.readAsDataURL(e.target.files[0]);
            setShowImageDialog(true);
        }
    };

    const onLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        imgRef.current = e.currentTarget;
        if (aspect) {
            const { width, height } = e.currentTarget;
            setCrop(makeAspectCrop({ unit: '%', width: 50, height: 50 }, aspect, width, height));
        }
    }, []);

    useEffect(() => {
        if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
            return;
        }

        const image = imgRef.current;
        const canvas = previewCanvasRef.current;
        const cropped = completedCrop;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return;
        }

        const pixelRatio = 1;

        canvas.width = cropped.width * pixelRatio * scaleX;
        canvas.height = cropped.height * pixelRatio * scaleY;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            cropped.x * scaleX,
            cropped.y * scaleY,
            cropped.width * scaleX,
            cropped.height * scaleY,
            0,
            0,
            cropped.width * scaleX,
            cropped.height * scaleY
        );
    }, [completedCrop]);

    return (
        <>
            <Stack sx={{ gap: 2 }}>
                <input
                    type="file"
                    accept="image/*"
                    onChange={onSelectFile}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                />
                <Button
                    variant="contained"
                    disableElevation
                    color="primary"
                    fullWidth
                    onClick={() => fileInputRef.current?.click()}
                >
                    Choose Photo
                </Button>

                {!!id && (
                    <Button
                        onClick={deleteImage}
                        variant="outlined"
                        fullWidth
                        color="error"
                        endIcon={<FontAwesomeIcon icon={faTrashAlt} size="1x" />}
                    >
                        Delete Photo
                    </Button>
                )}
            </Stack>
            <Dialog open={showImageDialog} fullScreen onClose={() => setShowImageDialog(false)}>
                <DialogTitle>Crop Photo</DialogTitle>
                <IconButton
                    onClick={() => setShowImageDialog(false)}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                    <FontAwesomeIcon icon={faXmark} size="1x" />
                </IconButton>
                <DialogContent>
                    {isUploadingImage && (
                        <Stack
                            sx={{
                                gap: 2.5,
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                            }}
                        >
                            <CircularProgress color="primary" />

                            <Typography variant="h4" sx={{ color: 'grey.900' }}>
                                Uploading image...
                            </Typography>
                        </Stack>
                    )}

                    {upImg && (
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={1}
                        >
                            <img
                                ref={imgRef}
                                src={upImg}
                                onLoad={onLoad}
                                style={{ maxHeight: '80dvh' }}
                                alt="Crop me"
                            />
                        </ReactCrop>
                    )}
                    <canvas
                        ref={previewCanvasRef}
                        style={{
                            visibility: 'hidden',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: Math.round(completedCrop?.width ?? 0),
                            height: Math.round(completedCrop?.height ?? 0),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Stack direction="row" sx={{ gap: 2, width: '100%' }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            onClick={() => setShowImageDialog(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            disableElevation
                            variant="contained"
                            color="primary"
                            disabled={
                                !completedCrop?.width ||
                                !completedCrop?.height ||
                                isUploadingImage ||
                                isDeletingImage
                            }
                            onClick={() => handleImageSave(previewCanvasRef.current, completedCrop)}
                        >
                            Use Photo
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddEditAvatarImage;
